import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useEffect, useState } from "react";

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);

interface WeekRange {
  label: string;
  start: Dayjs;
  end: Dayjs;
  disabled: boolean;
}

const generateWeekRanges = (monthStart: Dayjs): WeekRange[] => {
  const firstWeekStart = monthStart.startOf("month").startOf("isoWeek");
  const lastWeekEnd = monthStart.endOf("month").endOf("isoWeek");

  const now = dayjs();

  const weeks: WeekRange[] = [];
  let cursor = firstWeekStart;

  while (cursor.isBefore(lastWeekEnd)) {
    const weekStart = cursor.startOf("isoWeek");
    const weekEnd = cursor.endOf("isoWeek");

    weeks.push({
      label: `${weekStart.format("MMM D")} - ${weekEnd.format("MMM D")}`,
      start: weekStart,
      end: weekEnd,
      disabled: weekEnd.isBefore(now.startOf("day")),
    });

    cursor = cursor.add(1, "week");
  }

  return weeks;
};

const getMonthRangeLabel = (start: Dayjs, end: Dayjs): string => {
  const startMonth = start.format("MMM");
  const endMonth = end.format("MMM");
  const year = end.format("YYYY");
  return `${startMonth} - ${endMonth} ${year}`;
};

export const useWeeklyDates = ({ initialDate }: { initialDate: Dayjs }) => {
  const [currentWeek, setCurrentWeek] = useState<Dayjs>(
    initialDate.startOf("month"),
  );
  const [weekRanges, setWeekRanges] = useState<WeekRange[]>([]);
  const [currentMonthRangeLabel, setCurrentMonthRangeLabel] =
    useState<string>("");
  const [canGoBack, setCanGoBack] = useState<boolean>(false);
  const [canGoNext, setCanGoNext] = useState<boolean>(false);

  const now = dayjs();

  const goToPreviousWeeks = () => {
    if (canGoBack) {
      setCurrentWeek((prev) => prev.subtract(1, "month"));
    }
  };

  const goToNextWeeks = () => {
    if (canGoNext) {
      setCurrentWeek((prev) => prev.add(1, "month"));
    }
  };

  useEffect(() => {
    setWeekRanges(generateWeekRanges(currentWeek));

    setCanGoBack(
      currentWeek
        .subtract(1, "month")
        .endOf("month")
        .isSameOrAfter(now.startOf("isoWeek")),
    );

    setCanGoNext(currentWeek.add(1, "month").year() === now.year());
    // eslint-disable-next-line
  }, [currentWeek]);

  useEffect(() => {
    if (weekRanges.length > 0) {
      setCurrentMonthRangeLabel(
        getMonthRangeLabel(
          weekRanges[0].start,
          weekRanges[weekRanges.length - 1].end,
        ),
      );
    }
  }, [weekRanges]);

  return {
    weekRanges,
    currentMonthRangeLabel,
    canGoBack,
    canGoNext,
    goToPreviousWeeks,
    goToNextWeeks,
  };
};
